import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  Building2,
  Globe,
  Phone,
  CreditCard,
  Check,
  RefreshCw,
  ExternalLink,
  AlertCircle,
  Save,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import MSPLayout from '../components/MSPLayout';
import { getMSPSettings, updateMSPSettings, connectMSPStripe, disconnectMSPStripe } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function MSPSettings() {
  const { msp } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    website: '',
    phone: '',
  });
  const [stripeAccountId, setStripeAccountId] = useState('');
  const [connectingStripe, setConnectingStripe] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await getMSPSettings();
      setSettings(response.data);
      const mspData = response.data.msp || {};
      setFormData({
        company_name: mspData.company_name || '',
        website: mspData.website || '',
        phone: mspData.phone || '',
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMSPSettings(formData);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectStripe = async () => {
    if (!stripeAccountId) {
      toast.error('Please enter a Stripe account ID');
      return;
    }

    setConnectingStripe(true);
    try {
      await connectMSPStripe({ stripe_account_id: stripeAccountId });
      toast.success('Stripe account connected!');
      fetchSettings();
      setStripeAccountId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Stripe');
    } finally {
      setConnectingStripe(false);
    }
  };

  const handleDisconnectStripe = async () => {
    try {
      await disconnectMSPStripe();
      toast.success('Stripe account disconnected');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect Stripe');
    }
  };

  if (loading) {
    return (
      <MSPLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </MSPLayout>
    );
  }

  const mspData = settings?.msp || {};

  return (
    <MSPLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-slate-400">Manage your MSP account settings</p>
        </div>

        {/* Company Information */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Company Information
            </CardTitle>
            <CardDescription className="text-slate-400">
              Update your MSP company details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Company Name</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://yourcompany.com"
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 555-0123"
                  className="pl-10 bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </CardContent>
        </Card>

        {/* Stripe Connect */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Stripe Connect
            </CardTitle>
            <CardDescription className="text-slate-400">
              Connect your Stripe account to receive commission payouts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {mspData.stripe_connected ? (
              <>
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Check className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Stripe Connected</p>
                      <p className="text-sm text-slate-400">
                        Account ID: {mspData.stripe_account_id}
                      </p>
                    </div>
                    <Badge className="ml-auto bg-emerald-500/10 text-emerald-500">Active</Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-400">
                    Your commission payouts will be automatically deposited to this account.
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleDisconnectStripe}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    Disconnect
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-6 h-6 text-amber-500" />
                    <div>
                      <p className="text-white font-medium">Stripe Not Connected</p>
                      <p className="text-sm text-slate-400">
                        Connect Stripe to receive your 30% commission payouts
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-slate-800/50">
                    <p className="text-sm text-slate-400 mb-4">
                      <strong className="text-white">Mock Mode:</strong> In production, you would be redirected to 
                      Stripe Connect OAuth. For now, enter any Stripe account ID to simulate connection.
                    </p>
                    <div className="flex gap-3">
                      <Input
                        value={stripeAccountId}
                        onChange={(e) => setStripeAccountId(e.target.value)}
                        placeholder="acct_xxxxxxxxxxxxx"
                        className="bg-slate-900 border-slate-700 text-white"
                      />
                      <Button
                        onClick={handleConnectStripe}
                        disabled={connectingStripe}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {connectingStripe ? 'Connecting...' : 'Connect'}
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <ExternalLink className="w-4 h-4" />
                    <a
                      href="https://connect.stripe.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline"
                    >
                      Learn more about Stripe Connect
                    </a>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Commission Info */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Commission Structure</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-slate-800/50 text-center">
                <p className="text-4xl font-bold text-white mb-2">30%</p>
                <p className="text-sm text-slate-400">Your commission rate</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-800/50 text-center">
                <p className="text-4xl font-bold text-white mb-2">Monthly</p>
                <p className="text-sm text-slate-400">Payout frequency</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-800/50 text-center">
                <p className="text-4xl font-bold text-white mb-2">1st</p>
                <p className="text-sm text-slate-400">Of each month</p>
              </div>
            </div>
            <p className="text-sm text-slate-400 mt-4">
              Commission is calculated on the total subscription amount paid by your clients. 
              Payouts are processed automatically on the 1st of each month via Stripe Connect.
            </p>
          </CardContent>
        </Card>

        {/* Account ID */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between py-2 border-b border-slate-800">
              <span className="text-slate-400">MSP ID</span>
              <span className="text-slate-300 font-mono text-sm">{mspData.id}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-800">
              <span className="text-slate-400">Account Status</span>
              <Badge className="bg-emerald-500/10 text-emerald-500">
                {mspData.status || 'active'}
              </Badge>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-800">
              <span className="text-slate-400">Total Clients</span>
              <span className="text-white">{mspData.client_count || 0}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-400">Member Since</span>
              <span className="text-white">
                {mspData.created_at
                  ? new Date(mspData.created_at).toLocaleDateString()
                  : 'N/A'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </MSPLayout>
  );
}
